import { decamelizeKeys, camelize } from "humps";
import { parse } from "qs";
import { DEFAULT_ADULTS } from "../constants/hotelConstants";
import { AdultsKey } from "../constants/bookingConstants";
import { HotelChildrenSettingAllCategories } from "../constants/hotelChildrenSettingCategories";
import { parseDate } from "./dateUtil";
import { format, isDate } from "date-fns";
import { isObjectEmpty } from "./objectUtil";
import { Hotel } from "../types/hotel";

export const modalParamKeys = ["modal", "modal_room_id", "modal_plan_id"];

export function convertForCompareParam(param: any) {
  // クライアントでしか使用しないparamを取り除く
  const keysForClient = [
    "sort_by",
    "sort_direction",
    "list_type",
    "plan_option",
    "meal",
    "room_amenity",
    "room_property",
    "modal",
    "modal_room_id",
    "modal_plan_id",
  ];

  function convertValue(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => convertValue(item));
    } else if (typeof value === "number") {
      return String(value);
    } else if (typeof value === "object" && value !== null) {
      return convertForCompareParam(value);
    } else {
      return value;
    }
  }

  const newParam = { ...param };

  Object.keys(newParam).forEach((key) => {
    if (keysForClient.includes(key)) {
      delete newParam[key];
    } else {
      const value = newParam[key];
      if (value || typeof value === "number") {
        newParam[key] = convertValue(value);
      } else {
        delete newParam[key];
      }
    }
  });
  return newParam;
}

export function excludeModalParam(param: any) {
  const decamelizedParam = decamelizeKeys(param);
  const { modal, modal_plan_id, modal_room_id, ...listParam } =
    decamelizedParam;
  return listParam;
}

export function toObjFromLocationSearch() {
  if (typeof window === "undefined") {
    return {};
  }
  return window.location.search
    .substring(1)
    .split("&")
    .reduce(function (result, value) {
      const parts = value.split("=");
      if (parts[0])
        result[decodeURIComponent(parts[0])] = decodeURIComponent(parts[1]);
      return result;
    }, {});
}

export function toObjFromUrl(url: string) {
  const urlWithoutHash = url.split("#")[0];
  const data = urlWithoutHash.split("?");
  if (!data || data.length < 2) {
    return {};
  }
  const search = data[1];
  return parse(search);
}

export const defaultGuestsParam = (
  guests: any,
  oneRoomLimited?: boolean,
  defaultAdultsNum?: number,
) => {
  if (guests && guests.length > 0) {
    if (oneRoomLimited) {
      return [guests[0]];
    }
    return guests;
  }
  return [{ adults: defaultAdultsNum || DEFAULT_ADULTS }];
};

export const adjustGuestsParam = (
  param: any,
  childrenSettings?: Hotel.ChildrenSetting[],
  oneRoomLimited?: boolean,
  defaultAdultsNum?: number,
) => {
  // 旧パラメータをguestsに変換
  const newParam = { ...param };
  const guestParam = {};
  const guestKeys = [
    AdultsKey,
    ...Object.values(HotelChildrenSettingAllCategories),
  ];

  // 現在有効な子供設定だけを対象にする
  const childrenKeys = childrenSettings
    ? childrenSettings.map((childrenSetting) => {
        return camelize(childrenSetting.category);
      })
    : [];

  guestKeys.map((key) => {
    const camelizedKey = camelize(key);
    if (newParam[camelizedKey]) {
      if (camelizedKey === AdultsKey || childrenKeys.includes(camelizedKey)) {
        guestParam[camelizedKey] = newParam[camelizedKey];
      }
      delete newParam[camelizedKey];
    }
  });

  const roomKey = "rooms"; // 旧パラメータ
  const rooms = newParam[roomKey] ? Number(newParam[roomKey]) || 1 : 1;
  delete newParam[roomKey];

  // guestsパラメータが元のparamに無い かつ
  // 旧パラメータが含まれる場合だけ変換したguestParamを使用する
  if (newParam.guests || isObjectEmpty(guestParam)) {
    newParam.guests = defaultGuestsParam(
      newParam.guests,
      oneRoomLimited,
      defaultAdultsNum,
    );
  } else {
    newParam.guests = Array.from(Array(rooms)).map(() => guestParam);
  }
  return newParam;
};

export const defaultHotelParam = (
  param: any,
  childrenSettings?: Hotel.ChildrenSetting[],
  oneRoomLimited?: boolean,
  defaultAdultsNum?: number,
) => {
  const defaultCheckinDate = param.checkinDate
    ? parseDate(param.checkinDate)
    : null;
  const defaultCheckoutDate = param.checkoutDate
    ? parseDate(param.checkoutDate)
    : null;

  return {
    ...adjustGuestsParam(
      param,
      childrenSettings,
      oneRoomLimited,
      defaultAdultsNum,
    ),
    checkinDate: isDate(defaultCheckinDate)
      ? format(defaultCheckinDate, "yyyy/LL/dd")
      : null,
    checkoutDate: isDate(defaultCheckoutDate)
      ? format(defaultCheckoutDate, "yyyy/LL/dd")
      : null,
  };
};
